/**
 * ClemtexCategoryLinks - shopping entry point.
 *
 * Category cross-links ("Need parts?" / related categories) on www.clemtex.com,
 * option A (approved 2026-10-11):
 *   Category pages : a slim link bar under the H1; plus the full tile strip after
 *                    the product grid (one page of products) or an inline "+N more"
 *                    drawer under the bar (more than one page - infinite scroll).
 *   Product pages  : "Parts for this tool: <category> (N)" under Add to Cart, and a
 *                    panel (category tile + up to 4 featured parts) in the
 *                    related-items area.
 *
 * Data: /site/category-links.json, published from the Related Category Link
 * records (customrecord_cx_link) that the Commerce Category Editor's Related tab
 * manages. Kill switch: the file's "enabled": false -> nothing renders.
 *
 * Safe Mode: only the Extensibility API (container components) and the DOM -
 * no core modules at all. Plan: Commerce Categories/Cross-Link Mockups/Cross-Link Build Plan.md
 */
define('Clemtex.CategoryLinks', [
    'Clemtex.CategoryLinks.Rules',
    'Clemtex.CategoryLinks.Data',
    'Clemtex.CategoryLinks.Render'
], function (Rules, Data, Render) {
    'use strict';

    var state = { kind: null, key: null, redraw: null };
    var seq = 0;
    var observer = null;
    var guardTimer = null;

    function $(sel, root) { return (root || document).querySelector(sel); }
    function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
    function removeAll(selector) { $$(selector).forEach(function (n) { if (n.parentNode) { n.parentNode.removeChild(n); } }); }
    function insertAfter(ref, html) { ref.insertAdjacentHTML('afterend', html); }
    function log(msg, e) { if (window.console && window.console.warn) { window.console.warn('ClemtexCategoryLinks: ' + msg, e || ''); } }

    // --- counts ------------------------------------------------------------
    function fillCounts(root) {
        $$('[data-cx-count]', root).forEach(function (slot) {
            if (slot.getAttribute('data-cx-filled')) { return; }
            slot.setAttribute('data-cx-filled', '1');
            Data.count(slot.getAttribute('data-cx-count'), slot.getAttribute('data-cx-url')).then(function (n) {
                slot.textContent = Render.formatCount(n, slot.getAttribute('data-cx-format'));
            });
        });
    }
    function fillCountsWhenVisible(root) {
        if (!root) { return; }
        if (!('IntersectionObserver' in window)) { fillCounts(root); return; }
        var io = new window.IntersectionObserver(function (list) {
            list.forEach(function (en) { if (en.isIntersecting) { fillCounts(root); io.disconnect(); } });
        }, { rootMargin: '200px' });
        io.observe(root);
    }

    // --- re-render guard: the core redraws these views (infinite scroll, item
    // actions), which can wipe our nodes; put them back without refetching.
    function startGuard() {
        if (observer || !('MutationObserver' in window)) { return; }
        observer = new window.MutationObserver(function () {
            if (!state.redraw) { return; }
            clearTimeout(guardTimer);
            guardTimer = setTimeout(function () { if (state.redraw) { state.redraw(true); } }, 250);
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    // --- category pages -------------------------------------------------------
    function renderCategory(plp, payload) {
        var info = plp.getCategoryInfo && plp.getCategoryInfo();
        var catId = info && info.internalid;
        // afterShowContent can fire while the PLP still reports the PREVIOUS category
        // (seen on fast in-site navigation): ask the caller to retry shortly.
        if (!catId || !Rules.isCategoryPath(window.location.pathname, info.fullurl)) { return 'retry'; }
        var entries = Rules.entriesFor(payload, catId);
        if (!entries.length) { return false; }

        var pag = {};
        try { pag = plp.getPagination() || {}; } catch (e) { /* older core: treat as one page */ }
        var mode = Rules.placement(entries.length, pag.itemCount, pag.pageSize);
        var question = Rules.questionFor(payload, entries[0].rel);
        var groups = Rules.groupsFor(entries, payload.relations);

        var draw = function (fromGuard) {
            var h1 = $('.facets-facet-browse-category-heading-container h1');
            if (!h1) { return; }
            var needBar = !$('[data-cx-root="bar"]');
            var needBottom = mode === 'bar+bottom' && !$('[data-cx-root="bottom"]');
            var needDrawer = mode === 'bar+inline' && !$('[data-cx-root="drawer"]');
            if (!needBar && !needBottom && !needDrawer) { return; }
            var wasOpen = false;
            if (needBar || needDrawer) {
                var oldToggle = $('[data-cx-toggle]');
                wasOpen = !!(oldToggle && oldToggle.getAttribute('aria-expanded') === 'true');
                removeAll('[data-cx-root="bar"],[data-cx-root="drawer"]');
                insertAfter(h1, Render.bar(question, entries, mode));
                fillCounts($('[data-cx-root="bar"]'));
                if (mode === 'bar+inline') {
                    insertAfter($('[data-cx-root="bar"]'), Render.strip(groups, 'drawer'));
                    if (wasOpen) { toggleDrawer(true); }
                }
            }
            if (needBottom) {
                var grid = $('.facets-facet-browse-items');
                if (grid) {
                    removeAll('[data-cx-root="bottom"]');
                    insertAfter(grid, Render.strip(groups, 'bottom'));
                    fillCountsWhenVisible($('[data-cx-root="bottom"]'));
                }
            }
            if (fromGuard) { log('re-inserted after a core redraw'); }
        };
        state = { kind: 'plp', key: String(catId), redraw: draw };
        draw(false);
        return true;
    }

    function toggleDrawer(open) {
        var btn = $('[data-cx-toggle]');
        var drawer = $('[data-cx-root="drawer"]');
        if (!btn || !drawer) { return; }
        var isOpen = typeof open === 'boolean' ? open : btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) { drawer.removeAttribute('hidden'); fillCounts(drawer); } else { drawer.setAttribute('hidden', ''); }
    }

    // One delegated handler for the drawer toggle and the "more related" jump. Both are
    // <button>s (no href), and the listener runs in the capture phase, so the SC router's
    // own link handling never sees these clicks.
    function onClick(ev) {
        var t = ev.target && ev.target.closest ? ev.target.closest('[data-cx-toggle],[data-cx-jump]') : null;
        if (!t) { return; }
        ev.preventDefault();
        if (t.hasAttribute('data-cx-toggle')) { toggleDrawer(); return; }
        var target = document.getElementById(t.getAttribute('data-cx-jump'));
        if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }

    // --- product pages ---------------------------------------------------------
    function itemIdOf(pdp) {
        var info = pdp.getItemInfo && pdp.getItemInfo();
        var item = info && (info.item || info);
        return item && (item.internalid || item.id);
    }

    function renderProduct(pdp, payload) {
        var itemId = itemIdOf(pdp);
        var link = itemId && Rules.itemLinkFor(payload, itemId);
        if (!link) { return false; }
        var cards = null;
        var draw = function (fromGuard) {
            var page = $('.product-details-full');
            if (!page) { return; }   // full product page only, never the quick view
            var actions = $('.product-details-full-actions', page);
            // The related-items block is a sibling of .product-details-full, not inside it.
            var related = $('.product-details-full-content-related-items');
            var hasPanel = !!related;
            var line = $('[data-cx-root="pdp-line"]');
            // Related items render after the actions: upgrade the line with the jump link.
            if (line && hasPanel && !$('[data-cx-jump]', line)) { line.parentNode.removeChild(line); line = null; }
            if (actions && !line) {
                insertAfter(actions, Render.pdpLine(link, hasPanel));
                fillCounts($('[data-cx-root="pdp-line"]'));
            }
            if (related && !$('[data-cx-root="pdp-panel"]')) {
                related.insertAdjacentHTML('afterbegin', Render.pdpPanel(link, cards || []));
                fillCountsWhenVisible($('[data-cx-root="pdp-panel"]'));
            }
            if (fromGuard) { log('re-inserted after a core redraw'); }
        };
        state = { kind: 'pdp', key: String(itemId), redraw: draw };
        draw(false);
        if (link.featured.length) {
            var mySeq = seq;
            Data.cards(link.featured).then(function (list) {
                if (mySeq !== seq) { return; }
                cards = list;
                var box = $('[data-cx-cards]');
                if (box) { box.innerHTML = list.map(Render.partCard).join(''); }
            });
        }
        return true;
    }

    // --- wiring ------------------------------------------------------------------
    function reset() {
        state = { kind: null, key: null, redraw: null };
        removeAll('[data-cx-root]');
    }

    var RETRY_MS = 400;
    var RETRIES = 8;   // ~3 s: then it is a brand page / non-category view, not a race

    function onShow(kind, component) {
        var mySeq = ++seq;
        reset();
        Data.payload().then(function (payload) {
            if (mySeq !== seq || !payload || payload.enabled === false) { return; }
            var attempt = function (left) {
                if (mySeq !== seq) { return; }
                var result;
                try {
                    result = kind === 'plp' ? renderCategory(component, payload) : renderProduct(component, payload);
                } catch (e) {
                    log('render failed', e);
                    return;
                }
                if (result === 'retry' && left > 0) { setTimeout(function () { attempt(left - 1); }, RETRY_MS); }
            };
            attempt(RETRIES);
        });
    }

    return {
        mountToApp: function mountToApp(container) {
            try {
                var plp = container.getComponent('PLP');
                var pdp = container.getComponent('PDP');
                if (!plp && !pdp) { return; }
                Render.injectStyles();
                document.addEventListener('click', onClick, true);
                startGuard();
                if (plp && plp.on) { plp.on('afterShowContent', function () { onShow('plp', plp); }); }
                if (pdp && pdp.on) { pdp.on('afterShowContent', function () { onShow('pdp', pdp); }); }
            } catch (e) {
                log('mount failed', e);
            }
        }
    };
});
